import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link } from '@backstage/core-components';

const kbdSx = {
  bgcolor: 'action.hover',
  borderRadius: 0.5,
  px: 0.5,
  fontSize: '0.75rem',
  fontFamily: 'monospace',
  display: 'inline-block',
} as const;

type HintEntry = {
  keys: string[];
  label: string;
  ariaLabel: string;
};

// Bindings always shown in the footer.
const baseHints: HintEntry[] = [
  { keys: ['↑', '↓'], label: 'Navigate', ariaLabel: 'Navigate results' },
  { keys: ['↵', 'Enter'], label: 'Open', ariaLabel: 'Open result' },
  { keys: ['Esc'], label: 'Close', ariaLabel: 'Close' },
];

// Extra bindings shown only when the recents panel is visible.
const recentsHints: HintEntry[] = [
  { keys: ['^X'], label: 'Delete', ariaLabel: 'Delete highlighted recent' },
];

export type QuickSearchKeyboardHintsProps = {
  // When provided, a 'View all results' link is shown right-aligned in
  // the footer. Pass the full /search?query=… URL.
  viewAllHref?: string;
  // When true, the ^X Delete hint is shown (recents panel is visible).
  recentsActive?: boolean;
};

export const QuickSearchKeyboardHints = ({
  viewAllHref,
  recentsActive = false,
}: QuickSearchKeyboardHintsProps) => {
  const hints = recentsActive ? [...baseHints, ...recentsHints] : baseHints;
  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        borderTop: 1,
        borderColor: 'divider',
        px: 2,
        py: 1,
        flexWrap: 'wrap',
        flexShrink: 0, // never push into the scrollable results area
        gap: 2,
      }}
    >
      {hints.map(hint => (
        <Box
          key={hint.ariaLabel}
          role="group"
          aria-label={hint.ariaLabel}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          {hint.keys.map(key => (
            <Box key={key} component="kbd" sx={kbdSx}>
              {key}
            </Box>
          ))}
          <Typography variant="caption" color="text.secondary">
            {hint.label}
          </Typography>
        </Box>
      ))}

      {/* Spacer pushes the link to the far right */}
      <Box sx={{ flex: 1 }} />

      {viewAllHref && (
        <Typography
          variant="caption"
          component={Link}
          to={viewAllHref}
          noTrack
          color="primary"
          sx={{
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          View all results →
        </Typography>
      )}
    </Stack>
  );
};
